'use client';

// `useActiveNetwork()` — 3-state companion to `useNetworkMode()`.
//
// Returns the user's effective network as one of `'local' | 'devnet' |
// 'mainnet'`. Use this for INFRASTRUCTURE bindings — anything that
// configures a backend origin, RPC endpoint, query cache, or session
// cookie that is keyed by network:
//
//   • SdkProvider           (apiUrl from getNetworkConfig — different
//                            for local vs devnet)
//   • NetworkSyncProvider   (query-cache invalidation on origin change)
//   • NetworkAuthBridge     (re-establish session when backend origin
//                            differs)
//
// For UX/URL decisions (NetworkLink prefix, "demo mode" banner, etc.),
// use `useNetworkMode()` instead — that hook collapses 'local' into
// 'devnet' because at the user-visible level they're indistinguishable.
//
// ─── The rule (two lines) ──────────────────────────────────────────
//
//   1. If stored preference is `'mainnet'` AND the `mainnet_enabled`
//      gate is not settled ON AND this deployment configures devnet →
//      return `'devnet'`. The gate ONLY clamps mainnet; `'local'` is
//      engineer-only and bypasses the gate entirely (engineers running
//      with `_LOCAL_*` env vars know what they're doing).
//
//   2. Otherwise return the stored preference verbatim.
//
// The gate is the last settled value `<MainnetGateMirror>` stored. It is unknown
// (devnet) while Statsig loads, so the server render and the hydration
// render agree, and held through identity updates, so signing in or out
// never flips the network mid-flight. A team member moves to mainnet in one
// step when the gate settles on.
import { isNetworkConfigured } from './config';
import { useNetworkPresentation } from './presentation-context';
import { useNetworkStore } from './store';
import type { Network } from './types';
import { useMainnetEnabled } from './use-mainnet-enabled';
export function useActiveNetwork() {
  const presentation = useNetworkPresentation();
  const mainnetEnabled = useMainnetEnabled();
  const stored = useNetworkStore(_temp);
  if (presentation !== null) {
    return presentation.network;
  }
  if (stored === "mainnet" && !mainnetEnabled && isNetworkConfigured("devnet")) {
    return "devnet";
  }
  return stored;
}
function _temp(s) {
  return s.current;
}